import React, {useCallback, useMemo} from "react";
import {FaCircle} from "react-icons/fa";
import {AgGridReact} from "ag-grid-react";
import Button from "../../../../../components/button/button.jsx";
import {Modal} from "../../../../../components/modal/modal.jsx";
import {navigateHook} from "../../../../../hooks/use-navigate.js";
import projectUtils from "../../../../../utils/project-utils.js";
import aggridUtils from "../../../../../utils/aggrid-utils.js";
import {DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL} from "../../../../../utils/ag-grid-columns-utils.js";

const protectionText = (types = []) => types.map(type => String(type).replaceAll("_", " ")).join(", ");

const riskColor = (risk) => ({
    LOW: "green",
    MEDIUM: "#f6c10e",
    HIGH: "red",
}[risk] ?? "#676a6c");

const API_DESPACHO_WEB = import.meta.env.VITE_API_DESPACHO_WEB;

const LinkCell = ({value, onClick}) => (
    <button type="button" className="text-blue-700 cursor-pointer" onClick={onClick}>
        {value}
    </button>
);

const CountGrid = ({items, columnDefs}) => (
    <div className="min-h-0 flex-1">
        <AgGridReact
            rowData={items}
            columnDefs={columnDefs}
            defaultColDef={DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL}
            animateRows={false}
            suppressCellFocus
            gridOptions={{localeText: aggridUtils.localization()}}
            theme={aggridUtils.generalTheme()}
        />
    </div>
);

const AttendanceGrid = ({items = [], showRequester = true, navigate}) => {
    const onOpenAttendance = useCallback((id) => {
        navigate(`${API_DESPACHO_WEB}/attendance/form/${id}`, true);
    }, [navigate]);

    const columnDefs = useMemo(() => [
        {field: "protocol", headerName: "Protocolo", width: 140, comparator: aggridUtils.agGridNumberYearComparator,
            cellRenderer: (params) => <LinkCell value={params.value} onClick={() => onOpenAttendance(params.data.id)}/>,
        },
        {field: "start", headerName: "Data", width: 170, comparator: aggridUtils.agGridCompareDateTime},
        ...(showRequester ? [
            {field: "contactName", headerName: "Solicitante", minWidth: 150, flex: 1},
            {field: "phone", headerName: "Telefone", width: 140, valueFormatter: (params) => projectUtils.formatPhone(params.value)},
            {field: "cpf", headerName: "CPF", width: 140, valueFormatter: (params) => projectUtils.formatCpfToView(params.value)},
        ] : []),
        {field: "nature", headerName: "Natureza", minWidth: 180, flex: 1},
        {field: "address", headerName: "Endereço do fato", minWidth: 260, flex: 2},
    ], [showRequester, onOpenAttendance]);

    return (
        <CountGrid
            items={items}
            columnDefs={columnDefs}
        />
    );
};

const buildSosStatus = (value) => {
    switch (value) {
        case "PENDING": return "Pendente";
        case "ACTIVATED": return "Ativo";
        case "INACTIVATED": return "Inativo";
    }
}

const SosGrid = ({items = [], navigate}) => {
    const onOpenCitizen = useCallback((id) => {
        navigate(`/sos/citizens/form/${id}`, true);
    }, [navigate]);

    const columnDefs = useMemo(() => [
        {field: "lifeRisk", headerName: "Risco", width: 60,
            cellRenderer: (params) => (
                (params.data.lifeRisk || params.data.protectionTypes?.length)
                    ? <div className={"w-full flex justify-center items-center h-full"}><FaCircle color={riskColor(params.data.lifeRisk)}/></div>
                    : null),
        },
        { field: "id", headerName: "Id", width: 100, cellDataType: "number",
            cellRenderer: (params) => <LinkCell value={params.value} onClick={() => onOpenCitizen(params.data.id)}/>,
        },
        {field: "name", headerName: "Nome", minWidth: 160, flex: 1},
        {field: "protectionProgram", headerName: "Proteção", minWidth: 160, flex: 1},
        {field: "status", headerName: "Status", width: 130, valueFormatter: (params) => buildSosStatus(params.value)},
        {field: "phone", headerName: "Telefone", width: 140, valueFormatter: (params) => projectUtils.formatPhone(params.value)},
        {field: "cpf", headerName: "CPF", width: 140, valueFormatter: (params) => projectUtils.formatCpfToView(params.value)},
    ], [onOpenCitizen]);

    return <CountGrid items={items} columnDefs={columnDefs}/>;
};

const AttendanceCountModal = ({
    open,
    setOpen,
    type,
    data,
}) => {
    const navigate = navigateHook();
    const attendances = data?.attendances?.attendanceList ?? [];
    const sos = data?.sos ?? [];
    const isRequester = type === "requester";

    return (
        <Modal
            open={open}
            setOpen={setOpen}
            title={isRequester ? "Atendimentos do mesmo solicitante" : "Atendimentos do mesmo endereço"}
            className="w-11/12"
        >
            <Modal.Body className="flex h-[calc(100vh-137px)] flex-col gap-3 overflow-hidden">
                <section className="flex min-h-0 flex-[4] flex-col">
                    <AttendanceGrid items={attendances} showRequester={isRequester} navigate={navigate}/>
                </section>
                {isRequester && (
                    <>
                        <h3 className="text-base font-semibold text-gray-600">SOS do solicitante</h3>
                        <section className="flex min-h-0 flex-1 flex-col">
                            <SosGrid items={sos} navigate={navigate}/>
                        </section>
                    </>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default AttendanceCountModal;
